import type { SkippedEntry } from "@cloudflare/dofs";

import type { ExecEncoding, ExecSyncResult, KillSignal } from "../shell.js";
import type { TruncatedOutput } from "./output-spool.js";

export type WorkspaceRuntimeAccess = "read" | "read-write";

/** Per-call context the backend passes to every host module function. */
export interface WorkspaceModuleCallContext {
  /** Aborts when the call passes its deadline or the execution is cancelled. */
  readonly signal: AbortSignal;
  /** Epoch milliseconds after which the caller stops waiting for this call. */
  readonly deadline: number;
  /** Access level of the backend running the call. */
  readonly access: WorkspaceRuntimeAccess;
  /**
   * Resolve a path the isolate passed against the backend's root.
   * Rejects paths that escape the root or pass through a symlink.
   *
   * @param path - An absolute path, or one relative to the backend root.
   * @param options - Set `allowMissing` when the path may not exist yet.
   * @returns The confined absolute path.
   */
  resolvePath(path: string, options?: { readonly allowMissing?: boolean }): Promise<string>;
  /**
   * Identifies this call. When a replayable run is interrupted while the
   * call is in flight and runs it again on resume, the second call gets
   * the same id, so a function can use it as an idempotency key.
   */
  readonly callId: string;
  /**
   * Pause the run until a person or another system answers, and return
   * the answer. The run is released while it waits, survives a deploy,
   * and resumes once `workspace.runtime.respond()` answers this call:
   * it replays to this call, and this function runs again with the same
   * `callId`, this time getting the answer straight away. Anything the
   * function does before `interrupt()` therefore runs twice, so keep it
   * safe to repeat. Only a run with `onInterrupt: "replay"` can wait;
   * anywhere else this throws. A call can interrupt once, and a second
   * `interrupt()` throws. Experimental: it may change in any release.
   *
   * ```ts
   * postComment: async ([issue, body], ctx) => {
   *   const { approved } = await ctx.interrupt({ kind: "approval", issue, body });
   *   if (!approved) throw new Error("The comment was rejected.");
   *   return github.comment(issue, body, { idempotencyKey: ctx.callId });
   * }
   * ```
   *
   * @param payload What the answer is for, as `pending()` lists it.
   */
  interrupt<Answer = WorkspaceRuntimeValue>(payload: WorkspaceRuntimeValue): Promise<Answer>;
}

/**
 * One host function exported by a host module.
 *
 * `args` holds the arguments the isolate passed, decoded from the wire.
 * They come from untrusted code, so parse them before use. The function
 * may return a value or a promise of one. Arguments and results cross
 * the isolate boundary as real values through Workers RPC, not as
 * encoded text. The result must be JSON-compatible plain data: the
 * bridge checks it at runtime and treats `undefined` as `null`.
 */
export type WorkspaceModuleFunction = (
  args: readonly WorkspaceRuntimeValue[],
  context: WorkspaceModuleCallContext,
) => unknown;

/** Named functions a host module exports into the isolate. */
export type WorkspaceModuleFunctions = Readonly<Record<string, WorkspaceModuleFunction>>;

/** Workspace services a host module factory can build its functions from. */
export interface WorkspaceModuleHost {
  /** The Workspace's Git client. Throws on use when Git is not configured. */
  readonly git: import("../git/index.js").GitClient;
  /** The Workspace's Artifacts client. Throws on use when Artifacts is not configured. */
  readonly artifacts: import("../artifacts/index.js").ArtifactClient;
  /** The Workspace runtime, for running commands on other backends. */
  readonly runtime: import("./runtime.js").WorkspaceRuntime;
  readonly assets?: import("../assets/index.js").AssetsClient;
}

/**
 * Builds a host module's functions from the Workspace's services. The
 * backend calls it once when it connects to its Workspace. The
 * prebuilt modules in `@cloudflare/computer/modules/*` are factories.
 */
export interface WorkspaceModuleFactory {
  (host: WorkspaceModuleHost): WorkspaceModuleFunctions;
  /**
   * What the module does and how to call it, for a model. The
   * JavaScript backend adds it to its own description, which the exec
   * tool shows. Objects of functions are listed by their export names.
   */
  readonly description?: string;
}

/**
 * A module caller source can import.
 *
 * - A string is JavaScript source bundled into the isolate, with no host access.
 * - An object of functions is a host module. Its functions run in the
 *   Durable Object and each becomes a named export:
 *   `{ "ws:weather": { forecast } }` lets code write
 *   `import { forecast } from "ws:weather"`.
 * - A factory is a host module that needs the Workspace's Git client,
 *   Artifacts client, or runtime, such as `createGitModule()`.
 *
 * Host modules must use a `ws:*` specifier.
 */
export type WorkspaceModule = string | WorkspaceModuleFunctions | WorkspaceModuleFactory;

export type WorkspaceRuntimeValue =
  | null
  | boolean
  | number
  | string
  | WorkspaceRuntimeValue[]
  | { [key: string]: WorkspaceRuntimeValue };

export interface WorkspaceRuntimeStat {
  name: string;
  inode: number;
  mode: number;
  mtime: number;
  size: number;
  isFile: boolean;
  isDirectory: boolean;
  isSymbolicLink: boolean;
}

export interface WorkspaceRuntimeFilesystem {
  readFile(path: string): Promise<ReadableStream<Uint8Array>>;
  readFile(path: string, encoding: "utf8"): Promise<string>;
  stat(path: string): Promise<WorkspaceRuntimeStat>;
  lstat(path: string): Promise<WorkspaceRuntimeStat>;
  readlink(path: string): Promise<string>;
  readdir(
    path: string,
    options?: { limit?: number },
  ): Promise<
    Array<{
      name: string;
      isFile: boolean;
      isDirectory: boolean;
      isSymbolicLink: boolean;
    }>
  >;
  find(directory: string, pattern?: string): Promise<Array<{ path: string; type: "file" | "dir" }>>;
  ls(prefix: string): Promise<string[]>;
  grep(
    pattern: string,
    path: string,
    options?: { ignoreCase?: boolean },
  ): Promise<Array<{ path: string; line: number; text: string }>>;
  mkdir(path: string, options?: { recursive?: boolean }): Promise<void>;
  writeFile(
    path: string,
    content: string | Uint8Array,
    options?: { exclusive?: boolean },
  ): Promise<void>;
  rm(path: string, options?: { recursive?: boolean; force?: boolean }): Promise<void>;
  chmod(path: string, mode: number): Promise<void>;
  symlink(target: string, path: string): Promise<void>;
}

export interface WorkspaceRuntimeLoader {
  load(code: {
    compatibilityDate: string;
    compatibilityFlags?: string[];
    limits?: { cpuMs?: number };
    mainModule: string;
    modules: Record<string, string | { js?: string }>;
    globalOutbound?: Fetcher | null;
  }): {
    getEntrypoint(name?: string, options?: { limits?: { cpuMs?: number } }): unknown;
  };
}

export type WorkspaceRuntimeStatus = "completed" | "failed" | "cancelled";

type RuntimeChunk<E extends ExecEncoding> = E extends "utf8" ? string : Uint8Array;

export type WorkspaceRuntimeEvent<E extends ExecEncoding = undefined> =
  | { id: string; seq: number; name: "stdout"; value: RuntimeChunk<E> }
  | { id: string; seq: number; name: "stderr"; value: RuntimeChunk<E> }
  | {
      id: string;
      seq: number;
      name: "exit";
      code: number;
      result?: WorkspaceRuntimeValue;
      /** Output that passed the limits, and where it was saved in full. */
      truncated?: WorkspaceRuntimeTruncation;
    };

/**
 * Which streams of a run were cut to their last lines, and where each
 * was saved in full. A stream that fit its limits is absent.
 */
export interface WorkspaceRuntimeTruncation {
  /** Present when stdout was cut. */
  stdout?: TruncatedOutput;
  /** Present when stderr was cut. */
  stderr?: TruncatedOutput;
}

export interface WorkspaceRuntimeResult<E extends ExecEncoding = undefined> {
  status: WorkspaceRuntimeStatus;
  exitCode: number;
  stdout: E extends "utf8" ? string : Uint8Array;
  stderr: E extends "utf8" ? string : Uint8Array;
  value?: WorkspaceRuntimeValue;
  /**
   * Present when output passed the limits. `stdout` and `stderr` then
   * hold only the end of the output; the full output is in the file
   * each entry names.
   */
  truncated?: WorkspaceRuntimeTruncation;
  pushed: number;
  pulled: number;
  skipped: SkippedEntry[];
  sync: ExecSyncResult;
}

export interface WorkspaceRuntimeExecOptions<E extends ExecEncoding = undefined> {
  id?: string;
  backend?: string;
  cwd?: string;
  encoding?: E;
  input?: WorkspaceRuntimeValue;
  env?: Record<string, string>;
  stdin?: Uint8Array | string;
  timeoutMs?: number;
  sync?: "wait" | "defer";
  /**
   * What happens to this run if the Durable Object restarts while it
   * runs, as on a deploy or crash. Overrides the backend's
   * `onInterrupt`. Backends that can't resume a run ignore it.
   */
  onInterrupt?: WorkspaceRuntimeInterruptPolicy;
  /**
   * How much output `result()` keeps per stream. Output past it is cut
   * to its last lines and saved in full to a Workspace file. Overrides
   * the Workspace's `output` limits for this run; `false` keeps all
   * output and saves nothing.
   */
  output?: WorkspaceRuntimeOutputOptions | false;
}

/** Per-run output limits. Fields left out use the Workspace's limits. */
export interface WorkspaceRuntimeOutputOptions {
  /** The most lines kept per stream. Defaults to 2000. */
  maxLines?: number;
  /** The most bytes kept per stream. Defaults to 64 KiB. */
  maxBytes?: number;
}

export interface WorkspaceRuntimeGetOptions<E extends ExecEncoding = undefined> {
  backend?: string;
  encoding?: E;
  resume?: "tail" | "full" | number;
  /** Output limits for this replay, as for `exec`. */
  output?: WorkspaceRuntimeOutputOptions | false;
}

export interface WorkspaceRuntimeKillOptions {
  backend?: string;
  signal?: KillSignal;
}

export interface WorkspaceRuntimeDisposeOptions {
  backend?: string;
}

export interface WorkspaceRuntimeExecHandle<
  E extends ExecEncoding = undefined,
> extends ReadableStream<WorkspaceRuntimeEvent<E>> {
  readonly id: string;
  readonly backend: string;
  result(): Promise<WorkspaceRuntimeResult<E>>;
  kill(signal?: KillSignal): Promise<void>;
  [Symbol.dispose](): void;
}

export interface ModuleExecutionInput {
  id?: string;
  source: string;
  cwd?: string;
  input?: WorkspaceRuntimeValue;
  env?: Record<string, string>;
  stdin?: Uint8Array | string;
  timeoutMs?: number;
  sync?: "wait" | "defer";
  onInterrupt?: WorkspaceRuntimeInterruptPolicy;
}

/**
 * What a backend does with a run its Durable Object restarted under.
 * `fail` settles it as failed. `restart` runs it again from the start.
 * `replay` runs it again and answers the calls it already made from a
 * journal, so their effects aren't repeated. `restart` and `replay` are
 * experimental and may change in any release.
 */
export type WorkspaceRuntimeInterruptPolicy = "fail" | "restart" | "replay";

/**
 * A host module call waiting for an answer, from `ctx.interrupt()`.
 * Experimental: it may change in any release.
 */
export interface WorkspaceRuntimePendingCall {
  /** The execution the call belongs to. */
  readonly id: string;
  readonly backend: string;
  /** Pass this to `respond()` to answer the call. */
  readonly callId: string;
  /** What the call passed to `ctx.interrupt()`. */
  readonly payload: WorkspaceRuntimeValue;
  /** When the call started waiting, in milliseconds since the epoch. */
  readonly since: number;
}

export interface ModuleExecutionEnvelope {
  id: string;
  // Identity of the process-local backend runtime that owns this
  // execution. Omitted by backends whose execution state is durable or
  // shared with the host.
  runtimeId?: string;
  events: ReadableStream<WorkspaceRuntimeEvent>;
  // Sync bracket stats for a backend that pairs with a remote store.
  // The pre-exec push count is known when the envelope is created;
  // the post-drain pull outcome settles once `events` is consumed to
  // its end. Absent for backends that reuse the host store, whose
  // result reports zeroed stats.
  sync?: {
    pushed: number;
    outcome: Promise<{ applied: number; skipped: SkippedEntry[]; sync: ExecSyncResult }>;
  };
}

export interface WorkspaceModuleBackendHandle {
  exec(input: ModuleExecutionInput): Promise<ModuleExecutionEnvelope>;
  getExec(input: {
    id: string;
    after?: number | "tail";
    runtimeId?: string;
  }): Promise<ModuleExecutionEnvelope>;
  killExec(input: { id: string; signal?: KillSignal; runtimeId?: string }): Promise<void>;
  disposeExec(input: { id: string; runtimeId?: string }): Promise<void>;
  // Settle the runs a restart interrupted. A backend that recovers
  // when it connects needs nothing more here.
  recover?(): Promise<void>;
  // Calls waiting for an answer, and answering one.
  pending?(): Promise<Omit<WorkspaceRuntimePendingCall, "backend">[]>;
  respond?(input: { id: string; callId: string; answer: WorkspaceRuntimeValue }): Promise<void>;
  // Tear down a backend-owned transport. The command adapter omits
  // it: a command backend's transport is closed through its
  // BackendHandle, not through the adapter the runtime consumes.
  close?(): Promise<void>;
}

/** What a module backend receives when it connects to its Workspace. */
export type WorkspaceModuleBackendHost = import("../backend.js").WorkspaceBackendHost & {
  /** The Workspace runtime, handed to host modules. */
  readonly runtime: import("./runtime.js").WorkspaceRuntime;
  readonly assets?: import("../assets/index.js").AssetsClient;
};

export interface WorkspaceModuleBackend {
  readonly protocol: "module";
  readonly id: string;
  readonly type: string;
  readonly callable?: boolean;
  /** What the backend tells a model about itself. Shown by the exec tool. */
  readonly description?: string;
  connect(host: WorkspaceModuleBackendHost): Promise<WorkspaceModuleBackendHandle>;
}

export type WorkspaceRegisteredBackend =
  | import("../backend.js").WorkspaceBackend
  | WorkspaceModuleBackend;

export function isModuleBackend(
  backend: WorkspaceRegisteredBackend,
): backend is WorkspaceModuleBackend {
  return "protocol" in backend && backend.protocol === "module";
}
